import React from "react";
import { Mail } from "lucide-react";
import BrassRule from "@/components/BrassRule";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";

const CONTACT_EMAIL = "hello@viajadeverdad.com";

export default function Contact() {
  const { t, lang } = useLang();
  const c = t.contact_page;

  useSeo({
    title:
      lang === "es"
        ? "Contacto — Viaja de Verdad"
        : "Contact — Viaja de Verdad",
    description: c.sub,
    image: "/static/og/og-default.jpg",
    locale: lang === "es" ? "es_ES" : "en_US",
    type: "website",
  });

  return (
    <section
      data-testid="contact-page"
      className="relative bg-ivory min-h-[100svh] pt-32 md:pt-40 pb-28"
    >
      <div className="mx-auto max-w-3xl px-6 md:px-10">
        <p className="eyebrow text-emerald" data-testid="contact-eyebrow">
          {c.eyebrow}
        </p>
        <h1
          className="font-display text-charcoal mt-6 leading-[1.05] text-balance"
          style={{ fontSize: "clamp(2.5rem, 6vw, 4.75rem)" }}
          data-testid="contact-title"
        >
          {c.title}{" "}
          <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>
            {c.italic}
          </span>
        </h1>
        <BrassRule className="mt-10" width={60} />
        <p
          className="italic-quote text-charcoal/80 mt-10 max-w-xl text-balance"
          style={{ fontSize: "clamp(1.15rem, 1.4vw, 1.4rem)", lineHeight: 1.55 }}
        >
          {c.sub}
        </p>

        {/* Email row */}
        <div className="mt-16 md:mt-20">
          <p className="eyebrow text-charcoal/55" style={{ fontSize: "11px" }}>
            {c.reach_us}
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="mt-4 inline-flex items-center gap-3 text-charcoal hover:text-emerald link-underline"
            style={{ fontSize: "clamp(1.35rem, 2.2vw, 1.75rem)" }}
            data-testid="contact-email-link"
          >
            <Mail size={20} strokeWidth={1.4} />
            <span className="italic-quote">{CONTACT_EMAIL}</span>
          </a>
        </div>

        {/* Bases + Hours */}
        <div className="mt-16 grid sm:grid-cols-2 gap-10 sm:gap-12">
          <div>
            <p className="eyebrow text-charcoal/55" style={{ fontSize: "11px" }}>
              {c.bases_label}
            </p>
            <p
              className="font-display text-charcoal mt-3"
              style={{ fontSize: "clamp(1.15rem, 1.4vw, 1.4rem)" }}
              data-testid="contact-bases"
            >
              {c.bases}
            </p>
            <span className="block h-px bg-brass mt-4 w-10" aria-hidden />
          </div>
          <div>
            <p className="eyebrow text-charcoal/55" style={{ fontSize: "11px" }}>
              {c.hours_label}
            </p>
            <p
              className="font-display text-charcoal mt-3"
              style={{ fontSize: "clamp(1.15rem, 1.4vw, 1.4rem)" }}
              data-testid="contact-hours"
            >
              {c.hours}
            </p>
            <span className="block h-px bg-brass mt-4 w-10" aria-hidden />
          </div>
        </div>

        <p className="text-charcoal/70 mt-16 text-sm md:text-base max-w-md" data-testid="contact-note">
          {c.note}
        </p>
      </div>
    </section>
  );
}
